import { useEffect } from "react";
import { useAppStore } from "../stores/appStore";
import useUserStore from "../stores/userStore";
import {
  markAppOffline,
  checkConnectivityAndReload,
  resetConnectivityUiState,
} from "../utils/internetConnection";

const CONNECTIVITY_POLL_MS = 1500;

const useInternetConnection = () => {
  const { isOnline } = useAppStore();
  const token = useUserStore((state) => state.token);

  useEffect(() => {
    if (!token) {
      resetConnectivityUiState();
      return;
    }

    const handleOnline = () => {
      void checkConnectivityAndReload();
    };

    const handleOffline = () => {
      markAppOffline();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const { isOnline: appOnline, showReloadBanner } = useAppStore.getState();
      if (!appOnline && !showReloadBanner) {
        void checkConnectivityAndReload();
      }
    };

    if (!navigator.onLine) {
      markAppOffline();
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    void checkConnectivityAndReload();

    const pollId = window.setInterval(() => {
      const { isOnline: appOnline, showReloadBanner } = useAppStore.getState();

      if (!navigator.onLine && appOnline) {
        markAppOffline();
        return;
      }

      if (!appOnline && !showReloadBanner) {
        void checkConnectivityAndReload();
      }
    }, CONNECTIVITY_POLL_MS);

    let socketConnectHandler: (() => void) | null = null;

    const attachSocketListener = () => {
      const { getSocketInstance } = require("../utils/socket") as {
        getSocketInstance: () => import("socket.io-client").Socket | null;
      };
      const socket = getSocketInstance();
      if (!socket || socketConnectHandler) return;

      socketConnectHandler = () => {
        void checkConnectivityAndReload();
      };
      socket.on("connect", socketConnectHandler);
    };

    attachSocketListener();
    const socketAttachPollId = window.setInterval(attachSocketListener, 1000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(pollId);
      window.clearInterval(socketAttachPollId);

      if (socketConnectHandler) {
        const { getSocketInstance } = require("../utils/socket") as {
          getSocketInstance: () => import("socket.io-client").Socket | null;
        };
        const socket = getSocketInstance();
        socket?.off("connect", socketConnectHandler);
      }
    };
  }, [token]);

  return { isOnline };
};

export default useInternetConnection;
